import React, { useEffect, useState } from "react";
import Modal from "../../../../../../../components/modal";
import Spinner from "../../../../../../../components/Loaders/Spinner";
import { formatCurrency } from "../../../../../../../../components/helper/helper";
import { getMerchantDepositDetailData } from "../../../../../../../../api";
import style from "./detail-modal.module.scss";

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const DepositWaterfall = ({ detail, label }) => {
  const sales = toNumber(detail?.sale);
  const returns = toNumber(detail?.return);
  const netBatch =
    detail?.net_amount != null ? toNumber(detail?.net_amount) : sales - returns;

  // Fees, reserve and rejects are stored as negative values on the replica;
  // show their magnitudes as subtractions. net_fee is the pre-rolled total of
  // the individual fee columns, so it's the only fee figure we need here.
  const fees = Math.abs(toNumber(detail?.net_fee));
  const reserve = Math.abs(toNumber(detail?.reserve));
  const reject = Math.abs(toNumber(detail?.reject));
  const netDeposit =
    detail?.net_deposit != null
      ? toNumber(detail?.net_deposit)
      : netBatch - fees;
  const deposit =
    detail?.deposit != null
      ? toNumber(detail?.deposit)
      : detail?.net_billing_deposit != null
      ? toNumber(detail?.net_billing_deposit)
      : netDeposit - reserve - reject;

  const hasHolds = reserve > 0 || reject > 0;

  // Only surface the NET DEPOSIT subtotal when a reserve/reject splits it from
  // the final deposit; otherwise fees flow straight into DEPOSIT.
  const rows = [
    { label: "$ SALES", value: sales, total: true },
    { label: "RETURNS", value: returns, subtract: true, indent: true },
    { label: "NET BATCH", value: netBatch, total: true },
    { label: "FEES", value: fees, subtract: true, indent: true },
  ];
  if (hasHolds) {
    rows.push({ label: "NET DEPOSIT", value: netDeposit, total: true });
    if (reserve > 0)
      rows.push({ label: "RESERVE", value: reserve, subtract: true, indent: true });
    if (reject > 0)
      rows.push({ label: "REJECTS", value: reject, subtract: true, indent: true });
  }

  // The stored deposit can include billing fees, reserve releases or other
  // adjustments the fee/reserve/reject columns don't itemize, so the visible
  // deductions won't always bridge the last subtotal down to DEPOSIT. When they
  // don't, surface the difference as a single ADJUSTMENTS line rather than
  // letting the column silently fail to add up. A positive residual (credit)
  // shows as an addition, a negative one as a subtraction.
  const lastSubtotal = hasHolds ? netDeposit - reserve - reject : netBatch - fees;
  const adjustment = deposit - lastSubtotal;
  if (Math.abs(adjustment) >= 0.01) {
    rows.push({
      label: "ADJUSTMENTS",
      value: Math.abs(adjustment),
      subtract: adjustment < 0,
      add: adjustment > 0,
      indent: true,
    });
  }

  rows.push({ label: "DEPOSIT", value: deposit, total: true });

  return (
    <div className={style.waterfall}>
      {label && <span className={style.plan_label}>{label}</span>}
      <table className={style.calc}>
        <tbody>
          {rows.map((row, index) => {
            const beforeTotal = !row.total && rows[index + 1]?.total;
            return (
              <tr
                key={row.label}
                className={row.total ? style.total_row : undefined}
              >
                <td
                  className={`${style.calc_label} ${row.indent ? style.indent : ""}`}
                >
                  {row.label}
                </td>
                <td className={style.calc_op}>
                  {row.subtract ? "−" : row.add ? "+" : ""}
                </td>
                <td
                  className={`${style.calc_value} ${
                    beforeTotal ? style.underline : ""
                  }`}
                >
                  {formatCurrency(row.value)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const DepositDetailModal = ({ isOpen, onClose, data }) => {
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const mid = data?.mid;
  const bbhid = data?.bbhid;

  useEffect(() => {
    if (!isOpen || !mid || !bbhid) {
      setDetails([]);
      return;
    }

    let active = true;
    setLoading(true);
    getMerchantDepositDetailData({ mid, bbhid })
      .then((res) => {
        if (!active) return;
        setDetails(res.status && Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.log(err);
        if (active) setDetails([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isOpen, mid, bbhid]);

  if (!data) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} header="Deposit Details" size="sm">
      {loading ? (
        <div className={style.loader}>
          <Spinner size={32} />
        </div>
      ) : !bbhid ? (
        <p className={style.empty}>
          No settlement breakdown available for this deposit.
        </p>
      ) : details.length === 0 ? (
        <p className={style.empty}>No settlement breakdown found.</p>
      ) : (
        details.map((detail, index) => (
          <DepositWaterfall
            key={detail?.id ?? index}
            detail={detail}
            label={
              details.length > 1
                ? detail?.individual_plan
                  ? "Individual plan"
                  : "Combined plan"
                : null
            }
          />
        ))
      )}
    </Modal>
  );
};

export default DepositDetailModal;
